import { useState } from 'react';
import { AlertTriangle, RotateCcw, Users } from 'lucide-react';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { Alert, AlertDescription } from '@/components/ui/alert';
import { Separator } from '@/components/ui/separator';
import { ScrollArea } from '@/components/ui/scroll-area';
import { Badge } from '@/components/ui/badge';

interface SelectedPayslip {
  id: string;
  employeeName: string;
  grossPay: number;
  netPay: number;
}

interface IndividualPayslipRollbackDialogProps {
  selectedPayslips: SelectedPayslip[];
  totalPayslipsInRun: number;
  runNumber: string;
  open: boolean;
  onOpenChange: (open: boolean) => void;
  onConfirm: (payslipIds: string[], reason: string) => void;
  isRollingBack: boolean;
  formatCurrency: (amount: number) => string;
}

export function IndividualPayslipRollbackDialog({
  selectedPayslips,
  totalPayslipsInRun,
  runNumber,
  open,
  onOpenChange,
  onConfirm,
  isRollingBack,
  formatCurrency,
}: IndividualPayslipRollbackDialogProps) {
  const [reason, setReason] = useState('');

  const isAllSelected = selectedPayslips.length === totalPayslipsInRun;
  const totalGross = selectedPayslips.reduce((sum, p) => sum + p.grossPay, 0);
  const totalNet = selectedPayslips.reduce((sum, p) => sum + p.netPay, 0);

  const handleConfirm = () => {
    if (!reason.trim() || selectedPayslips.length === 0) return;
    onConfirm(
      selectedPayslips.map((p) => p.id),
      reason.trim()
    );
  };

  const handleClose = () => {
    if (!isRollingBack) {
      setReason('');
      onOpenChange(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={handleClose}>
      <DialogContent className="sm:max-w-[550px] max-h-[85vh] flex flex-col">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2 text-destructive">
            <AlertTriangle className="h-5 w-5" />
            Rollback Selected Payslips
          </DialogTitle>
          <DialogDescription>
            Rolling back {selectedPayslips.length} of {totalPayslipsInRun} payslips from run #{runNumber}
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-4 py-4 overflow-y-auto flex-1 min-h-0">
          {isAllSelected && (
            <Alert>
              <Users className="h-4 w-4" />
              <AlertDescription>
                <strong>Tip:</strong> You've selected all payslips. Consider using the full "Rollback Run" 
                option instead for a cleaner audit trail.
              </AlertDescription>
            </Alert>
          )}

          <Alert variant="destructive">
            <AlertTriangle className="h-4 w-4" />
            <AlertDescription>
              <strong>Warning:</strong> This action cannot be undone. Selected payslips and their 
              line items will be permanently deleted. Leave accruals will be reversed.
            </AlertDescription>
          </Alert>

          <Separator />

          {/* Selected Employees List */}
          <div className="space-y-2">
            <div className="flex items-center justify-between">
              <h4 className="font-medium text-sm">Selected Employees</h4>
              <Badge variant="secondary">{selectedPayslips.length} selected</Badge>
            </div>
            <ScrollArea className="h-[120px] w-full border rounded-md p-2">
              <div className="space-y-1">
                {selectedPayslips.map((payslip) => (
                  <div 
                    key={payslip.id} 
                    className="flex items-center justify-between text-sm py-1 px-2 rounded hover:bg-muted/50"
                  >
                    <span className="font-medium">{payslip.employeeName}</span>
                    <span className="text-muted-foreground">
                      {formatCurrency(payslip.netPay)}
                    </span>
                  </div>
                ))}
              </div>
            </ScrollArea>
          </div>

          {/* Summary */}
          <div className="grid grid-cols-2 gap-2 text-sm bg-muted/50 rounded-md p-3">
            <div className="text-muted-foreground">Total Gross:</div>
            <div className="font-medium text-right">{formatCurrency(totalGross)}</div>
            <div className="text-muted-foreground">Total Net:</div>
            <div className="font-medium text-right">{formatCurrency(totalNet)}</div>
          </div>

          <Separator />

          {/* Reason Input */}
          <div className="space-y-2">
            <Label htmlFor="individual-rollback-reason" className="text-destructive">
              Reason for Rollback <span className="text-destructive">*</span>
            </Label>
            <Textarea
              id="individual-rollback-reason"
              placeholder="Please provide a detailed reason for rolling back these payslips (e.g., incorrect rates applied, wrong employee selected, etc.)"
              value={reason}
              onChange={(e) => setReason(e.target.value)}
              rows={3}
              disabled={isRollingBack}
              className="resize-none"
            />
            {!reason.trim() && (
              <p className="text-xs text-muted-foreground">
                A reason is required to proceed with the rollback
              </p>
            )}
          </div>

          <Alert>
            <AlertDescription className="text-xs">
              After rollback, you can regenerate payslips for these employees if needed.
              The rollback will be logged in the audit trail.
            </AlertDescription>
          </Alert>
        </div>

        <DialogFooter>
          <Button variant="outline" onClick={handleClose} disabled={isRollingBack}>
            Cancel
          </Button>
          <Button
            variant="destructive"
            onClick={handleConfirm}
            disabled={!reason.trim() || selectedPayslips.length === 0 || isRollingBack}
          >
            {isRollingBack ? (
              <>
                <RotateCcw className="mr-2 h-4 w-4 animate-spin" />
                Rolling Back...
              </>
            ) : (
              <>
                <RotateCcw className="mr-2 h-4 w-4" />
                Rollback {selectedPayslips.length} Payslip{selectedPayslips.length !== 1 ? 's' : ''}
              </>
            )}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
